import { useCallback, useEffect, useRef } from 'react';

import type { DraftController, DraftValue } from '../assistant/draftActions';
import { drainCrudDraft, registerDraftController, unregisterDraftController } from '../assistant/crudRegistry';

import { useFeatureToggle } from './useFeatureToggle';

/** How often a mounted page looks again for a proposal its form was not ready for. */
const DRAIN_RETRY_MS = 100;

/**
 * Lets the assistant open this page's create form filled in with what it proposes, while the page is mounted.
 *
 * The page passes the fields its create form actually has and the opener that fills it in; both are read
 * through this on every attempt, so what answers is the page as it is now. A proposal that arrived while the
 * user was elsewhere is drained here as the page comes up, retrying rather than running once, because a page
 * mounts before the data its form needs has loaded.
 *
 * Gated on `enableAssistant`, like the page context: with the flag off nothing is registered, so a tenant
 * without Ohm has no form the assistant can reach.
 */
export const useCrudDraft = (
  entityType: string | undefined,
  fields: readonly string[],
  openWith: (values: Record<string, DraftValue>) => void,
): void => {
  const enabled = useFeatureToggle(['enableAssistant']) && entityType !== undefined;

  // Read at call time rather than captured, so a fresh proposal reaches the page's current opener and the
  // registration does not churn on every render the page does.
  const current = useRef({ fields, openWith });
  current.current = { fields, openWith };

  const readController = useCallback(
    (): DraftController => ({
      fields: () => [...current.current.fields],
      open: values => current.current.openWith(values),
    }),
    [],
  );

  useEffect(() => {
    if (!enabled || entityType === undefined) return;

    registerDraftController(entityType, readController);

    let retry: ReturnType<typeof setTimeout> | undefined;
    const drain = (): void => {
      if (drainCrudDraft(entityType, readController)) return;
      retry = setTimeout(drain, DRAIN_RETRY_MS);
    };
    drain();

    return () => {
      clearTimeout(retry);
      unregisterDraftController(entityType, readController);
    };
  }, [enabled, entityType, readController]);
};
